/* /wheel sound. Web Audio, one sample. Everything else is synthesised on a
   small chain: voices -> a short generated room -> compressor -> master.

   Cues:
   - start(): the press. The whoosh sample from public/wheel/whoosh.mp3,
               pitched down and rolled off so it sits deeper, as the room
               goes dark; the synthesised rising whoosh is the fallback.
   - tick(i):  the flapper riding a peg. Three layers (body, tock, hit) mixed
               by speed: fast pegs are bright and light, the slow last pegs
               are dull and heavy, the way a real blade sounds when it drops
               off a pin at walking pace.
   - land():   the stop. A thump with a wooden knock on top. Not called at
               the moment: the win samples carry the stop on their own.
   - win(big): the reveal. Samples from public/wheel: win.wav for any prize,
               win-big.wav for the headline ones. Both fetched once on
               unlock; the synthesised stings stay as the fallback.

   The AudioContext is created lazily on the first user gesture (the page
   calls unlock() from the spin button) so autoplay policy never blocks it.
   Every call is a no-op on the server and before unlock. */

export interface WheelAudio {
  /** The wheel taking torque. */
  start: () => void
  /** Flapper over a peg. intensity 0..1 follows the wheel's angular speed. */
  tick: (intensity: number) => void
  /** Landing: thump plus a wooden knock. */
  land: () => void
  /** The reveal sting. */
  win: (big: boolean) => void
  /** Tension before a reveal: filtered noise climbing for `ms`. */
  riser: (ms: number) => void
  /** A short, quick swish: something small flying past. */
  swish: () => void
  /** Create/resume the context. Call from a user gesture. */
  unlock: () => void
  /** Fetch and decode the samples ahead of the first press. Safe on mount:
      the context is created suspended and decoding needs no gesture. */
  preload: () => void
}

type Ctx = AudioContext

export function createWheelAudio(): WheelAudio {
  let ctx: Ctx | null = null
  let noise: AudioBuffer | null = null
  let dry: GainNode | null = null
  let wet: GainNode | null = null
  let lastTickAt = 0
  const samples = new Map<string, AudioBuffer>()
  const loading = new Set<string>()
  const BIG_WIN_URL = "/wheel/win-big.wav"
  const WIN_URL = "/wheel/win.wav"
  const WHOOSH_URL = "/wheel/whoosh.mp3"

  function context(): Ctx | null {
    if (typeof window === "undefined") return null
    if (!ctx) {
      const Impl =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Impl) return null
      ctx = new Impl()
      buildChain(ctx)
    }
    return ctx
  }

  /** master <- compressor <- (dry, room <- wet). */
  function buildChain(c: Ctx) {
    const master = c.createGain()
    master.gain.value = 0.9
    const comp = c.createDynamicsCompressor()
    comp.threshold.value = -18
    comp.knee.value = 12
    comp.ratio.value = 4
    comp.attack.value = 0.004
    comp.release.value = 0.16
    comp.connect(master).connect(c.destination)

    dry = c.createGain()
    dry.gain.value = 1
    dry.connect(comp)

    // A small room: 0.55 s of decaying noise as the impulse.
    const room = c.createConvolver()
    const len = Math.floor(c.sampleRate * 0.55)
    const ir = c.createBuffer(2, len, c.sampleRate)
    for (let ch = 0; ch < 2; ch++) {
      const d = ir.getChannelData(ch)
      for (let i = 0; i < len; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6)
      }
    }
    room.buffer = ir
    wet = c.createGain()
    wet.gain.value = 0.22
    wet.connect(room).connect(comp)
  }

  /** 120 ms of white noise, built once, sliced by every burst. */
  function noiseBuffer(c: Ctx): AudioBuffer {
    if (noise) return noise
    const len = Math.floor(c.sampleRate * 0.12)
    noise = c.createBuffer(1, len, c.sampleRate)
    const data = noise.getChannelData(0)
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
    return noise
  }

  function ready(): Ctx | null {
    const c = ctx
    if (!c || c.state !== "running" || !dry || !wet) return null
    return c
  }

  function out(node: AudioNode, room = 0) {
    if (!dry || !wet) return
    node.connect(dry)
    if (room > 0) {
      const send = ctx!.createGain()
      send.gain.value = room
      node.connect(send).connect(wet)
    }
  }

  /** Fetch and decode a sample once; silently keeps the synth on failure. */
  function loadSample(c: Ctx, url: string) {
    if (samples.has(url) || loading.has(url)) return
    loading.add(url)
    fetch(url)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(r.statusText))))
      .then((buf) => c.decodeAudioData(buf))
      .then((decoded) => samples.set(url, decoded))
      .catch(() => undefined)
      .finally(() => loading.delete(url))
  }

  function playSample(c: Ctx, buffer: AudioBuffer, vol: number, room: number) {
    const src = c.createBufferSource()
    src.buffer = buffer
    const g = c.createGain()
    g.gain.value = vol
    src.connect(g)
    out(g, room)
    src.start(c.currentTime)
  }

  function preload() {
    const c = context()
    if (!c) return
    loadSample(c, BIG_WIN_URL)
    loadSample(c, WIN_URL)
    loadSample(c, WHOOSH_URL)
  }

  function unlock() {
    const c = context()
    if (!c) return
    if (c.state === "suspended") void c.resume()
    preload()
  }

  function start() {
    const c = ready()
    if (!c) return
    const t = c.currentTime
    const sample = samples.get(WHOOSH_URL)
    if (sample) {
      // Deeper: slower playback and a lowpass taking the air off the top.
      const src = c.createBufferSource()
      src.buffer = sample
      src.playbackRate.value = 0.82
      const lp = c.createBiquadFilter()
      lp.type = "lowpass"
      lp.frequency.value = 2400
      const g = c.createGain()
      g.gain.value = 0.42
      src.connect(lp).connect(g)
      out(g, 0.35)
      src.start(t)
      return
    }
    // Fallback rising whoosh: noise through a bandpass sweeping up over 1.2 s.
    const src = c.createBufferSource()
    src.buffer = noiseBuffer(c)
    src.loop = true
    const bp = c.createBiquadFilter()
    bp.type = "bandpass"
    bp.Q.value = 1.4
    bp.frequency.setValueAtTime(180, t)
    bp.frequency.exponentialRampToValueAtTime(1400, t + 1.2)
    const g = c.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.5)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4)
    src.connect(bp).connect(g)
    out(g, 0.5)
    src.start(t)
    src.stop(t + 1.45)
  }

  function swish() {
    const c = ready()
    if (!c) return
    const t = c.currentTime
    const sample = samples.get(WHOOSH_URL)
    if (sample) {
      const src = c.createBufferSource()
      src.buffer = sample
      src.playbackRate.value = 1.45 + Math.random() * 0.15
      const g = c.createGain()
      g.gain.value = 0.22
      src.connect(g)
      out(g, 0.3)
      src.start(t)
      return
    }
    const src = c.createBufferSource()
    src.buffer = noiseBuffer(c)
    src.loop = true
    const bp = c.createBiquadFilter()
    bp.type = "bandpass"
    bp.Q.value = 1.2
    bp.frequency.setValueAtTime(600, t)
    bp.frequency.exponentialRampToValueAtTime(2800, t + 0.35)
    const g = c.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.12)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4)
    src.connect(bp).connect(g)
    out(g, 0.4)
    src.start(t)
    src.stop(t + 0.42)
  }

  function riser(ms: number) {
    const c = ready()
    if (!c) return
    const t = c.currentTime
    const dur = ms / 1000
    const src = c.createBufferSource()
    src.buffer = noiseBuffer(c)
    src.loop = true
    const bp = c.createBiquadFilter()
    bp.type = "bandpass"
    bp.Q.value = 2.2
    bp.frequency.setValueAtTime(220, t)
    bp.frequency.exponentialRampToValueAtTime(2600, t + dur)
    const g = c.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(0.2, t + dur * 0.85)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.08)
    src.connect(bp).connect(g)
    out(g, 0.5)
    src.start(t)
    src.stop(t + dur + 0.1)
    // A pulse under it, quickening toward the end.
    let at = t
    let gap = 0.42
    while (at < t + dur) {
      const o = c.createOscillator()
      o.type = "sine"
      o.frequency.value = 110
      const og = c.createGain()
      og.gain.setValueAtTime(0.0001, at)
      og.gain.exponentialRampToValueAtTime(0.18, at + 0.01)
      og.gain.exponentialRampToValueAtTime(0.0001, at + 0.12)
      o.connect(og)
      out(og, 0.2)
      o.start(at)
      o.stop(at + 0.14)
      at += gap
      gap = Math.max(0.09, gap * 0.82)
    }
  }

  function tick(intensity: number) {
    const c = ready()
    if (!c) return
    const i = Math.min(1, Math.max(0, intensity))
    const t = c.currentTime
    // Ticks closer than 18 ms smear into a buzz; drop them.
    if (t - lastTickAt < 0.018) return
    lastTickAt = t
    const detune = 1 + (Math.random() - 0.5) * 0.08
    const vol = 0.10 + 0.30 * i

    // Body: the blade's own note, heavier when slow.
    const body = c.createOscillator()
    body.type = "sine"
    body.frequency.setValueAtTime(420 * detune, t)
    body.frequency.exponentialRampToValueAtTime(220 * detune, t + 0.05)
    const bodyGain = c.createGain()
    bodyGain.gain.setValueAtTime(vol * (0.55 + 0.45 * (1 - i)), t)
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.045 + 0.05 * (1 - i))
    body.connect(bodyGain)
    out(bodyGain, 0.35)
    body.start(t)
    body.stop(t + 0.12)

    // Tock: a pitched knock, brighter when fast.
    const tock = c.createOscillator()
    tock.type = "triangle"
    tock.frequency.value = (1500 + 900 * i) * detune
    const tockGain = c.createGain()
    tockGain.gain.setValueAtTime(vol * 0.7, t)
    tockGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.014)
    tock.connect(tockGain)
    out(tockGain, 0.25)
    tock.start(t)
    tock.stop(t + 0.03)

    // Hit: a noise burst through a bandpass, the blade skidding off the pin.
    const hit = c.createBufferSource()
    hit.buffer = noiseBuffer(c)
    const bp = c.createBiquadFilter()
    bp.type = "bandpass"
    bp.frequency.value = 2200 + 2400 * i
    bp.Q.value = 0.9
    const hitGain = c.createGain()
    hitGain.gain.setValueAtTime(vol * 0.5, t)
    hitGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.022 + 0.02 * (1 - i))
    hit.connect(bp).connect(hitGain)
    out(hitGain, 0.3)
    hit.start(t, Math.random() * 0.06)
    hit.stop(t + 0.06)
  }

  function land() {
    const c = ready()
    if (!c) return
    const t = c.currentTime

    // Thump: sine 110 -> 42 Hz over 260 ms.
    const thump = c.createOscillator()
    thump.type = "sine"
    thump.frequency.setValueAtTime(110, t)
    thump.frequency.exponentialRampToValueAtTime(42, t + 0.26)
    const thumpGain = c.createGain()
    thumpGain.gain.setValueAtTime(0.6, t)
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3)
    thump.connect(thumpGain)
    out(thumpGain, 0.4)
    thump.start(t)
    thump.stop(t + 0.32)

    // Knock: the blade coming to rest against wood.
    const knock = c.createBufferSource()
    knock.buffer = noiseBuffer(c)
    const bp = c.createBiquadFilter()
    bp.type = "bandpass"
    bp.frequency.value = 900
    bp.Q.value = 2.5
    const knockGain = c.createGain()
    knockGain.gain.setValueAtTime(0.35, t)
    knockGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.09)
    knock.connect(bp).connect(knockGain)
    out(knockGain, 0.6)
    knock.start(t)
    knock.stop(t + 0.1)
  }

  /** One note: triangle plus a quiet octave sine, soft attack, room on it. */
  function note(c: Ctx, freq: number, at: number, dur: number, vol: number) {
    for (const [type, mul, v] of [
      ["triangle", 1, 1],
      ["sine", 2, 0.35],
    ] as const) {
      const osc = c.createOscillator()
      osc.type = type
      osc.frequency.value = freq * mul
      const g = c.createGain()
      g.gain.setValueAtTime(0.0001, at)
      g.gain.exponentialRampToValueAtTime(vol * v, at + 0.025)
      g.gain.exponentialRampToValueAtTime(0.0001, at + dur)
      osc.connect(g)
      out(g, 0.7)
      osc.start(at)
      osc.stop(at + dur + 0.02)
    }
  }

  function win(big: boolean) {
    const c = ready()
    if (!c) return
    const t = c.currentTime
    const C5 = 523.25
    const E5 = 659.25
    const G5 = 783.99
    const C6 = 1046.5
    const E6 = 1318.5
    const G6 = 1568.0
    if (!big) {
      const sample = samples.get(WIN_URL)
      if (sample) {
        playSample(c, sample, 0.8, 0.15)
        return
      }
      // Fallback: three up and a held top.
      note(c, C5, t, 0.28, 0.16)
      note(c, E5, t + 0.1, 0.28, 0.16)
      note(c, G5, t + 0.2, 0.3, 0.16)
      note(c, C6, t + 0.32, 0.9, 0.2)
      return
    }
    const sample = samples.get(BIG_WIN_URL)
    if (sample) {
      playSample(c, sample, 0.9, 0.15)
      return
    }
    // Fallback build: a run up two octaves, a chord hit, then a shimmer on top.
    const run = [C5, E5, G5, C6, E6, G6]
    run.forEach((f, k) => note(c, f, t + k * 0.085, 0.3, 0.14))
    const chordAt = t + run.length * 0.085 + 0.05
    for (const f of [C5, E5, G5, C6]) note(c, f, chordAt, 1.6, 0.16)
    // Shimmer: fast quiet notes on top, a bit random, over 1.2 s.
    for (let k = 0; k < 10; k++) {
      const f = [C6, E6, G6, 2093][k % 4] * (1 + (Math.random() - 0.5) * 0.004)
      note(c, f, chordAt + 0.2 + k * 0.11, 0.22, 0.05)
    }
    // Floor: a low swell under it.
    const low = c.createOscillator()
    low.type = "sine"
    low.frequency.value = C5 / 4
    const lowGain = c.createGain()
    lowGain.gain.setValueAtTime(0.0001, chordAt)
    lowGain.gain.exponentialRampToValueAtTime(0.22, chordAt + 0.3)
    lowGain.gain.exponentialRampToValueAtTime(0.0001, chordAt + 1.8)
    low.connect(lowGain)
    out(lowGain, 0.5)
    low.start(chordAt)
    low.stop(chordAt + 1.85)
  }

  return { start, tick, land, win, riser, swish, unlock, preload }
}

/** Shared instance so the page can unlock() from the spin button and the
    wheel can tick/land on the same context. */
export const wheelAudio: WheelAudio = createWheelAudio()
